import { useState, useEffect } from "react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "@/hooks/use-toast";
import { Loader2, Trash2, AlertTriangle, Store } from "lucide-react";
import { updateShop, deleteShop } from "@/lib/api";
import { getOwnerShops, setOwnerShops } from "@/lib/auth";
import type { Shop } from "@shared/schema";

interface ShopEditModalProps {
  shop: Shop | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onShopUpdated: () => void;
  ownerId: string;
}

export default function ShopEditModal({ 
  shop, 
  open, 
  onOpenChange, 
  onShopUpdated,
  ownerId 
}: ShopEditModalProps) {
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    owner: "",
    username: "",
    password: "",
    confirmPassword: "",
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  
  // Cargar datos del shop cuando se abre el modal
  useEffect(() => {
    if (shop && open) {
      setFormData({
        name: shop.name,
        address: shop.address,
        owner: shop.owner,
        username: shop.username,
        password: "",
        confirmPassword: "",
      });
      setDeleteMode(false);
      setDeleteConfirm("");
    }
  }, [shop, open]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validaciones
    if (formData.password && formData.password !== formData.confirmPassword) {
      toast({
        title: "Error",
        description: "Las contraseñas no coinciden",
        variant: "destructive"
      });
      return;
    }
    
    if (formData.password && formData.password.length < 6) {
      toast({
        title: "Error",
        description: "La contraseña debe tener al menos 6 caracteres",
        variant: "destructive"
      });
      return;
    }
    
    if (!shop) return;
    
    setIsLoading(true);
    
    try {
      // Preparar datos para actualizar
      const updates: any = {
        name: formData.name,
        address: formData.address,
        owner: formData.owner,
        username: formData.username,
      };
      
      if (formData.password) {
        updates.password = formData.password;
      }
      
      await updateShop(shop.id, updates);
      
      toast({
        title: "¡Tienda actualizada!",
        description: `La tienda "${formData.name}" ha sido actualizada correctamente.`,
      });
      
      // Actualizar localStorage
      const shops = getOwnerShops();
      const updatedShops = shops.map(s => 
        s.id === shop.id ? { ...s, ...updates } : s
      );
      setOwnerShops(updatedShops);
      
      // Cerrar modal y refrescar
      onShopUpdated();
      onOpenChange(false);
      
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "No se pudo actualizar la tienda",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDelete = async () => {
    if (!shop) return;
    
    // Confirmación extra
    if (deleteConfirm !== "ELIMINAR") {
      toast({
        title: "Error",
        description: 'Debes escribir "ELIMINAR" para confirmar',
        variant: "destructive"
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      await deleteShop(shop.id);
      
      toast({
        title: "¡Tienda eliminada!",
        description: `La tienda "${shop.name}" ha sido eliminada correctamente.`,
      });
      
      // Actualizar localStorage
      const shops = getOwnerShops();
      const updatedShops = shops.filter(s => s.id !== shop.id);
      setOwnerShops(updatedShops);
      
      // Cerrar modal y refrescar
      onShopUpdated();
      onOpenChange(false);
      
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "No se pudo eliminar la tienda",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const toggleDeleteMode = () => {
    setDeleteMode(!deleteMode);
    setDeleteConfirm("");
  };
  
  if (!shop) return null;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        {deleteMode ? (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                <DialogTitle>Eliminar Tienda</DialogTitle>
              </div>
              <DialogDescription className="pt-2">
                Esta acción no se puede deshacer. La tienda <strong>"{shop.name}"</strong> y todos sus datos serán eliminados permanentemente.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-2">
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Se eliminarán: inventario, ventas, clientes y configuración.
                </AlertDescription>
              </Alert>
              
              <div className="space-y-2">
                <Label htmlFor="deleteConfirm">
                  Escribe <strong className="font-bold">ELIMINAR</strong> para confirmar:
                </Label>
                <Input
                  id="deleteConfirm"
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  placeholder="ELIMINAR"
                  className="font-mono border-destructive/50 focus-visible:ring-destructive"
                />
                <p className="text-xs text-muted-foreground">
                  Esta acción eliminará {shop.name} permanentemente
                </p>
              </div>
            </div>
            
            <DialogFooter className="flex flex-col sm:flex-row gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={toggleDeleteMode}
                disabled={isLoading}
                className="sm:flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={handleDelete}
                disabled={isLoading || deleteConfirm !== "ELIMINAR"}
                className="sm:flex-1"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Eliminar
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2">
                <Store className="h-5 w-5 text-primary" />
                <DialogTitle>Editar Tienda</DialogTitle>
              </div>
              <DialogDescription>
                Modifica los datos de <strong>{shop.name}</strong>
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre de la Tienda *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="address">Dirección *</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  className="w-full"
                />
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="owner">Encargado *</Label>
                  <Input
                    id="owner"
                    value={formData.owner}
                    onChange={handleChange}
                    required
                    className="w-full"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="username">Usuario *</Label>
                  <Input
                    id="username"
                    value={formData.username}
                    onChange={handleChange}
                    required
                    className="w-full"
                  />
                </div>
              </div>
              
              <div className="border-t pt-4 space-y-4">
                <div className="space-y-2">
                  <h4 className="font-medium text-sm">Cambiar Contraseña (opcional)</h4>
                  <p className="text-xs text-muted-foreground">
                    Deja vacío si no quieres cambiar la contraseña
                  </p>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">Nueva Contraseña</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="Mínimo 6 caracteres"
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirmar</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="Repite la contraseña"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
              
              <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 pt-4">
                <div className="flex flex-col sm:flex-row w-full sm:w-auto gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    disabled={isLoading}
                    className="sm:flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={toggleDeleteMode}
                    disabled={isLoading}
                    className="border-destructive/20 text-destructive hover:bg-destructive/10 hover:text-destructive sm:flex-1"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Eliminar
                  </Button>
                </div>
                <Button 
                  type="submit" 
                  disabled={isLoading}
                  className="w-full sm:w-auto"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    "Guardar Cambios"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}